import { Trans } from '@lingui/macro'
import { authRoles, checkAuth } from 'app/auth/authRoles'
import { getAccountsMap } from 'app/services/sfAuth/sfData/sfAccount'
import { getContactsMapByFlow } from 'app/services/sfAuth/sfData/sfContact'
import { getFormPage, getReusableFormPage } from 'app/services/sfAuth/sfData/sfForms'
import { opportunitiesStages } from 'app/services/sfAuth/sfData/sfOpportunity'
import reactImageSize from 'react-image-size'
import { formComponentTypes } from './components/formComponentTypes'
import { cloneInjectableElement } from './editor/InjectablesElementsPanel'
import { baseLogoSize, mapFormElements } from './Form'
import { getDisabledIds } from './formHelpersConditions'
import { getInitialValues } from './formHelpersFormik'
import { constructValidationSchema } from './formHelpersValidation'
import { connectedObjectQuery } from './formsHelpersQueries'

/**
 * Fetches and processes form data, and reusable form components
 * @param {Object} params - Function parameters
 * @param {string} [params.reloadId] - ID used for reloading the form
 * @param {Object} [params.renderData] - Preloaded form data
 * @param {boolean} [params.isPreview=false] - Flag for preview mode
 * @param {Object} [params.user] - Current user object
 * @param {string} [params.formId] - Form ID
 * @param {Object} [params.fundingStreams] - Funding streams configuration
 * @param {Object} [params.enqueueSnackbar] - EnqueueSnackbar function
 * @param {string} [params.mainObject] - Main object type
 * @param {boolean} [params.returnPdf] - Flag for PDF return
 * @param {Function} [params.handleObjectMissing] - Function to handle object missing
 * @param {string} [params.id] - Object ID
 * @param {Object} [params.formikRef] - Formik reference
 * @param {Object} [params.organization] - Organization object
 * @param {string} [params.formType] - Form type
 * @param {boolean} [params.shouldHandleMultiuser] - Flag for should handle multiuser
 * @param {boolean} [params.requireValidationScheme] - Flag for should validation scheme be required
 * @param {boolean} [params.forceDisabled] - Flag for should the form be disabled
 * @param {Object} [params.params] - URL parameters
 * @returns {Promise} Promise that resolves with processed form data
 */
export const fetchData = async ({
  reloadId,
  renderData,
  isPreview,
  user,
  formId,
  fundingStreams,
  enqueueSnackbar,
  mainObject,
  returnPdf,
  handleObjectMissing,
  id,
  formikRef,
  organization,
  formType,
  requireValidationScheme,
  shouldHandleMultiuser,
  forceDisabled = false,
  params,
  pathname
}) => {
  const output = {
    formType
  }

  try {
    // Fetch initial data in parallel
    const [result, reusableFormPage] = await Promise.all([
      renderData ? Promise.resolve(renderData) : getFormPage(formId),
      getReusableFormPage()
    ])

    // Get language version
    const overrideFormLanguage = result?.overrideFormLanguage
    const { language } = user
    const langVersion_ = overrideFormLanguage || language?.split('_')[0] || 'en'
    const langVersion =
      overrideFormLanguage || (result?.translatedFor?.[langVersion_]?.value ? langVersion_ : 'en')
    output.langVersion = langVersion
    output.overrideFormLanguage = overrideFormLanguage

    // Process initial form data
    const processedResult = await processInitialFormData({
      result,
      reusableFormPage
    })

    // Preload imports in form elements if necessary
    await preloadFormElements(processedResult)

    if (!formType) {
      if (processedResult.readOnly) {
        formType = 'printable'
      } else if (processedResult.showPdfDownload) {
        formType = 'pdf'
      } else {
        formType = 'editable'
      }
    }
    const pdfDisplay = formType.includes('pdf')
    // Set form view settings
    const supported = processedResult.supportedFormType || [formType]
    output.supportedFormViews = supported
    output.notSupportedError = !supported.includes(formType)
    output.pdfDisplay = pdfDisplay
    output.formViewType = formType
    output.readOnly = formType === 'printable'
    output.data = processedResult
    output.disabled = forceDisabled

    // Handle connected objects
    if (processedResult.objectsConnected?.length > 0) {
      const fetchUrl = reloadId || id

      if (fetchUrl) {
        // Validate connected objects
        const { noId, newFetchUrl } = validateConnectedObjects(
          processedResult.objectsConnected,
          fetchUrl,
          isPreview,
          user
        )
        if (noId) {
          output.initialValuesError = { message: 'NO_ID_PROVIDED', type: 'Loading error' }
          return output
        }
        if (newFetchUrl !== fetchUrl) {
          output.initialValuesError = { message: 'NO_ID_PROVIDED', type: 'Loading error' }
          return output
        }

        try {
          // Query connected objects
          const { connectedMap, describeMap } = await connectedObjectQuery(processedResult, {
            handleObjectMissing,
            langVersion,
            id: fetchUrl,
            enqueueSnackbar
          })

          const programManagerSession = params?.organizationId && checkAuth(authRoles.pm, user.role)

          // Process connected objects if not in PDF display
          const { stage, shouldDisable, preventAccess, wrongAccountRole, configuration, error } =
            processConnectedObjects({
              connectedMap,
              result: processedResult,
              isPreview,
              user,
              mainObject,
              returnPdf,
              opportunitiesStages,
              formId,
              fundingStreams,
              disabled: forceDisabled
            })
          output.error = error
          output.wrongAccountRole = wrongAccountRole
          output.preventAccess = preventAccess
          output.stage = stage
          output.configuration = configuration

          if (preventAccess) {
            return output
          }

          if (requireValidationScheme) {
            output.validationSchema = constructValidationSchema({
              data: processedResult,
              describeMap,
              langVersion,
              formikRef
            })
          }
          output.disabled = shouldDisable || programManagerSession || forceDisabled
          const elementsMap = mapFormElements(processedResult, langVersion)

          // We check what won't be shown in the form due to conditions based on SF values
          // This is done to avoid throwing an initial value invalid error if the element is not used anyway
          const disabledFromSFConditions = getDisabledIds({
            sections: result.sections,
            elementsMap,
            values: null,
            langVersion,
            connectedMap,
            describeMap,
            errors: {},
            pathname
          })
          output.disabledFromSFConditions = disabledFromSFConditions

          // Get initial values for contacts and accounts mapping
          const fakeInitialValues = getInitialValues({
            enqueueSnackbar,
            data: processedResult,
            disabledFromSFConditions,
            connectedMap
          })

          output.connectedMap = connectedMap

          if (fakeInitialValues.error) {
            output.initialValuesError = fakeInitialValues.error
            return output
          }

          // Process contacts and accounts mapping
          const contacts = []
          const accounts = []
          Object.keys(elementsMap).forEach((key) => {
            const question = elementsMap[key]
            const value = fakeInitialValues[key]
            if (question.elementType === 'connectContact') {
              if (!contacts.includes(value) && value) {
                contacts.push(value)
              }
            } else if (question.elementType === 'connectAccount') {
              if (!accounts.includes(value) && value) {
                accounts.push(value)
              }
            }
          })

          // Get contacts and accounts maps
          const [contactsMap, accountsMap] = await (contacts.length > 0 || accounts.length > 0
            ? Promise.all([getContactsMapByFlow(contacts), getAccountsMap(accounts)])
            : Promise.resolve([{}, {}]))
          output.elementsMap = elementsMap
          output.describeMap = describeMap

          // Get final initial values
          const initialValues = getInitialValues({
            data: processedResult,
            disabledFromSFConditions,
            enqueueSnackbar,
            connectedMap,
            contactsMap,
            accountsMap
          })
          output.initialValues = initialValues

          if (initialValues.error) {
            output.initialValuesError = initialValues.error
            return output
          }

          if (pdfDisplay) {
            return output
          }

          // Handle multiuser functionality
          if (shouldHandleMultiuser) {
            return handleMultiuser({
              processedResult,
              id,
              user,
              organization,
              programManagerSession,
              output,
              initialValues,
              preventAccess
            })
          }
        } catch (error) {
          console.error('no object found', error, processedResult, fetchUrl)
          output.initialValuesError = { message: 'Unknown', type: 'Loading error' }
          return output
        }
      } else {
        if (requireValidationScheme) {
          const { describeMap } = await connectedObjectQuery(processedResult, {
            returnOnlyDescribe: true,
            handleObjectMissing,
            langVersion,
            enqueueSnackbar
          })
          output.validationSchema = constructValidationSchema({
            data: processedResult,
            describeMap,
            langVersion,
            formikRef
          })
        }
      }
    }

    // Handle case when no connected objects
    const initialValues = getInitialValues({
      data: processedResult,
      enqueueSnackbar,
      disabledFromSFConditions: [],
      connectedMap: {}
    })
    output.connectedMap = {}

    if (initialValues.error) {
      output.initialValuesError = initialValues.error
      return output
    }
    output.initialValues = initialValues
    output.data = processedResult
    return output
  } catch (error) {
    console.error('error loading form', error)
    output.error = error
    return output
  }
}

/**
 * Processes reusable components and creates a lookup map
 * @param {Array} reusableComponents - List of reusable components
 * @returns {Object} Map of reusable components
 */
export const processReusableComponents = (reusableComponents) => {
  const reusableComponentsMap = {}
  const addToMap = (item) => {
    reusableComponentsMap[item.id] = item
    if (item.elements) {
      item.elements.forEach((child) => addToMap(child))
    }
  }
  reusableComponents.forEach((component) => addToMap(component))
  return reusableComponentsMap
}

/**
 * Updates injectable items with their corresponding reusable components
 * @param {Object} item - Form item to update
 * @param {Object} reusableComponentsMap - Map of reusable components
 */
export const updateInjectedItems = (item, reusableComponentsMap) => {
  if (item.elements) {
    item.elements.forEach((element, index) => {
      if (element.injectableId) {
        if (reusableComponentsMap[element.injectableId]) {
          const updated = cloneInjectableElement({
            item: element,
            componentsMap: reusableComponentsMap
          })
          item.elements[index] = updated
        } else {
          delete element.injectableId
          delete element.injectableName
        }
      }
      updateInjectedItems(element, reusableComponentsMap)
    })
  }
}

/**
 * Processes logo dimensions for PDF header and footer
 * @param {Object} result - Form result object
 * @param {number} baseLogoSize - Base logo size for calculations
 * @returns {Promise} Promise that resolves with updated result
 */
export const processLogoDimensions = async (result, baseLogoSize) => {
  for (const item of ['header', 'footer']) {
    if (result?.pdfProps?.[item]?.logoUrl) {
      const logoUrl = result?.pdfProps?.[item]?.logoUrl
      const logoSize = result?.pdfProps?.[item]?.logoSize || baseLogoSize
      const { width, height } = await reactImageSize(logoUrl)
      if (width >= height) {
        const wRel = width / logoSize
        result.pdfProps[item].logoDimensions = {
          width: width / wRel,
          height: height / wRel
        }
      } else {
        const hRel = height / logoSize
        result.pdfProps[item].logoDimensions = {
          width: width / hRel,
          height: height / hRel
        }
      }
    }
  }
  return result
}

/**
 * Validates connected objects and processes their IDs
 * @param {Array} objectsConnected - Array of connected objects
 * @param {string} fetchUrl - URL for fetching connected objects
 * @param {boolean} isPreview - Whether in preview mode
 * @param {Object} user - Current user object
 * @returns {Object} Object containing validation results
 */
export const validateConnectedObjects = (objectsConnected, fetchUrl, isPreview, user) => {
  const objArray = fetchUrl.split(';')
  let noId = false

  objArray.forEach((string, index) => {
    if (string.includes('NO_OBJECT_ID')) {
      noId = true
      console.error(string)
    }
    const ident = string.split('=')[0]
    const objInfo = objectsConnected.find((obj) => obj.identId === ident)
    if (objInfo?.type === 'User' && !isPreview) {
      objArray[index] = `${ident}=${user.userId}`
    }
  })

  return { noId, newFetchUrl: objArray.join(';') }
}

/**
 * Process connected objects and determine form state
 * @param {Object} params - Parameters
 * @param {Object} params.connectedMap - Map of connected objects
 * @param {Object} params.result - Form result object
 * @param {boolean} params.isPreview - Whether in preview mode
 * @param {Object} params.user - Current user object
 * @param {string} params.mainObject - Main object type
 * @param {boolean} params.returnPdf - Whether to return PDF
 * @param {Object} params.opportunitiesStages - Opportunity stages constants
 * @param {string} params.formId - Form ID
 * @param {boolean} params.disabled - Whether to disable form
 * @returns {Object} Object containing stage and disabled state
 */
export const processConnectedObjects = ({
  connectedMap,
  result,
  isPreview,
  user,
  mainObject,
  returnPdf,
  opportunitiesStages,
  formId,
  fundingStreams,
  disabled
}) => {
  let preventAccess = false
  let stage
  let shouldDisable = false
  let wrongAccountRole
  let configuration
  let error

  Object.values(connectedMap).forEach((data) => {
    const obj = data?.sfObject
    const additionalInfo = data?.additionalInfo
    const objName = obj.attributes.type

    fundingStreams?.forEach((config) => {
      const sameForm = config.form && config.form === formId
      const sameRecordType = obj.RecordType && obj.RecordType.Id === config.recordType
      if (sameForm && sameRecordType) {
        configuration = config
      }
    })

    if (
      mainObject !== 'Review_Assignement__c' &&
      obj.UserRecordAccess &&
      !obj.UserRecordAccess.HasEditAccess &&
      !isPreview
    ) {
      shouldDisable = true
    }

    if (result.readOnly || returnPdf || disabled) {
      shouldDisable = true
    }

    if (!isPreview) {
      if (objName === 'Opportunity') {
        if (mainObject === 'Opportunity') {
          stage = obj.StageName
          if (
            obj.StageName !== opportunitiesStages.IN_PROGRESS &&
            obj.StageName !== opportunitiesStages.MORE_INFO_REQUIERED
          ) {
            shouldDisable = true
          }
        }

        if (obj.ProcessInstances?.records) {
          if (obj.ProcessInstances.records.some((process) => process.Status === 'Pending')) {
            error = <Trans>Application is Locked: Contact the Administrator</Trans>
            shouldDisable = true
          }
        }
      } else if (objName === 'FGM_Base__Grantee_Report__c') {
        stage = obj.FGM_Base__Status__c
        if (!['Requested', 'More info required'].includes(stage)) {
          shouldDisable = true
        }
      } else if (objName === 'Account' && result.restrictAccessForRoles) {
        let userRole
        additionalInfo.accountMembers.some((member) => {
          if (member.UserId === user.userId) {
            userRole = member.TeamMemberRole
            return true
          }
          return false
        })

        if (result.restrictAccessForRoles[userRole] || !userRole) {
          const restrictType = result.restrictAccessForRoles[userRole]
          if (restrictType === 'preventAccessBlock') {
            error = <Trans>Your member role has insufficient access to view this page!</Trans>
            preventAccess = true
          } else if (restrictType === 'disable') {
            shouldDisable = true
            wrongAccountRole = 'warning'
          } else if (restrictType === 'preventAccessMessage') {
            wrongAccountRole = 'prevent'
          }
        }
      } else if (objName === 'Pre_Qualification__c') {
        if (mainObject === 'Pre_Qualification__c') {
          stage = obj.Stage__c
          if (
            stage !== opportunitiesStages.IN_PROGRESS &&
            stage !== opportunitiesStages.MORE_INFO_REQUIERED
          ) {
            shouldDisable = true
          }
        }
      } else if (objName === 'Resource__c') {
        if (mainObject === 'Resource__c') {
          stage = obj.Stage__c
          if (stage !== 'In progress' && stage !== opportunitiesStages.MORE_INFO_REQUIERED) {
            shouldDisable = true
          }
        }
      }
    }
  })

  return { stage, shouldDisable, preventAccess, wrongAccountRole, configuration, error }
}

/**
 * Processes initial form data and sets up form state
 * @param {Object} params - Parameters object
 * @param {Object} params.result - Form result object
 * @param {Object} params.reusableFormPage - Reusable form page data
 * @returns {Object} Updated result object
 */
/**
 * Loads lazy resources (e.g. census geo data) of element types used in the form,
 * declared with `preload` in formComponentTypes
 * @param {Object} data - Form data with sections
 * @returns {Promise}
 */
export const preloadFormElements = (data) => {
  const elementTypes = new Set()
  const collectTypes = (elements = []) =>
    elements.forEach((element) => {
      if (element.elementType) {
        elementTypes.add(element.elementType)
      }
      if (Array.isArray(element.elements)) {
        collectTypes(element.elements)
      }
    })
  ;(data?.sections || []).forEach((section) => collectTypes(section.elements))
  return Promise.all(
    [...elementTypes]
      .map((type) => formComponentTypes[type]?.preload)
      .filter(Boolean)
      .map((preload) => preload())
  )
}

/**
 * Loads lazy resources of all element types - for the form editor, where any element can be added
 * @returns {Promise}
 */
export const preloadAllFormElements = () =>
  Promise.all(
    Object.values(formComponentTypes)
      .map((type) => type.preload)
      .filter(Boolean)
      .map((preload) => preload())
  )

export const processInitialFormData = async ({ result, reusableFormPage }) => {
  console.log('form loaded', result)

  const filterAvailableElements = (elements) => {
    return elements
      .filter(
        (element) =>
          (element.elementType && formComponentTypes[element.elementType]) || element.elements
      )
      .map((element) => {
        const newElement = { ...element }
        if (Array.isArray(newElement.elements)) {
          newElement.elements = filterAvailableElements(newElement.elements)
        }
        return newElement
      })
  }

  result.sections = result.sections.map((section) => {
    const newSection = { ...section }
    if (Array.isArray(section.elements)) {
      newSection.elements = filterAvailableElements(section.elements)
    }
    return newSection
  })

  // Process reusable components
  const reusableComponents = reusableFormPage?.config.injectableComponents || []
  const reusableComponentsMap = processReusableComponents(reusableComponents)

  // Update injectable items
  result.sections.forEach((section) => {
    updateInjectedItems(section, reusableComponentsMap)
  })

  // Process logo dimensions
  return await processLogoDimensions(result, baseLogoSize)
}

/**
 * Handles multiuser functionality
 * @param {Object} params - Parameters object
 * @param {Object} params.processedResult - Processed form result
 * @param {string} params.id - Object ID
 * @param {Object} params.user - Current user object
 * @param {Object} params.organization - Organization object
 * @param {boolean} params.programManagerSession - Flag for program manager session
 * @param {Object} params.output - Output object
 * @param {Object} params.initialValues - Initial values
 * @param {boolean} params.shouldDisable - Flag for should disable state
 * @param {boolean} params.preventAccess - Flag for prevent access state
 * @returns {Object} Updated output object
 */
function handleMultiuser({
  processedResult,
  id,
  user,
  organization,
  programManagerSession,
  output,
  initialValues,
  shouldDisable,
  preventAccess
}) {
  const multiuser =
    Boolean(processedResult.enableMultiuser && id) &&
    (checkAuth(authRoles.tester, user.role) ||
      organization.additionalFeatures.includes('multiuser_testing')) &&
    !processedResult.readOnly &&
    (!shouldDisable || programManagerSession) &&
    !preventAccess

  output.initialValues = initialValues
  output.multiuser = multiuser
  return output
}
